// src/app/components/admin/email-recipients-setting.tsx
//
// Platform setting: WHO the platform mail account may write to, per brand.
//
// Upmode sends from one shared mailbox, and both the Upmode Agent chat and the
// AI Workflows "Email Results" block let a signed-in user name the recipient.
// The backend (app/services/email_guard.py) refuses any address that is not
// approved for the brand, so without this screen the only address anyone can
// mail is their own.
//
// A brand is normally configured by DOMAIN — "anyone at sunbit.com" — because
// that is the real unit: a brand mails its own people, and maintaining a list
// of individual mailboxes goes stale immediately. Specific addresses stay
// available underneath for the one-off case (a contractor on another domain).
//
// What a brand can reach is the union of THREE things, and the UI shows all
// three, because showing fewer would understate the permission:
//   * team members — always allowed, their own address, changed under
//     Associate Brands rather than here
//   * approved domains — every mailbox at each
//   * approved addresses — exactly these
//
// Stored in app_config, so a change applies to the very next send with no
// deploy. Admin-only; the backend returns 403 on write for non-admins, and
// rejects a public webmail domain (approving gmail.com would permit every
// Gmail address in existence).

import { useEffect, useMemo, useState } from "react";
import {
  Mail, Check, Loader2, Search, ChevronDown, ChevronRight,
  Users, Plus, X, AlertTriangle, Globe,
} from "lucide-react";

import {
  upmodeAgentApi,
  type BrandRecipientsRow,
  type OrphanRecipientsRow,
} from "@/app/services/upmode-agent/upmode-agent-api";

/** Both mirror the backend's shape checks. The server decides — these only
 *  stop an obviously bad value before a round-trip. */
const EMAIL_RE = /^[^@\s,;<>]+@[^@\s,;<>]+\.[^@\s,;<>]+$/;
const DOMAIN_RE = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/;

/** Accept what an admin will actually type: "@sunbit.com", "https://sunbit.com/",
 *  "SUNBIT.COM". Matches normalize_domain() on the server. */
function normalizeDomain(value: string): string {
  return value
    .trim().toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, "")
    .replace(/^@+/, "")
    .split("/")[0]
    .trim()
    .replace(/\.+$/, "");
}

export function EmailRecipientsSetting() {
  const [rows, setRows] = useState<BrandRecipientsRow[]>([]);
  const [orphans, setOrphans] = useState<OrphanRecipientsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    upmodeAgentApi
      .emailRecipientsOverview()
      .then((data) => {
        if (!alive) return;
        setRows(data.brands || []);
        setOrphans(data.orphaned || []);
      })
      .catch((e) => alive && setErr(String(e?.message || e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.brand_name.toLowerCase().includes(q) ||
        r.aliases.some((a) => a.toLowerCase().includes(q)) ||
        r.approved_domains.some((d) => d.includes(q)) ||
        r.approved_emails.some((e) => e.includes(q)) ||
        r.members.some((m) => m.username.includes(q)),
    );
  }, [rows, query]);

  /** Replace one brand's row in place, so an edit does not disturb scroll
   *  position or collapse the other brands. */
  function applySaved(brandKey: string, domains: string[], emails: string[]) {
    setRows((prev) =>
      prev.map((r) =>
        r.brand_key === brandKey
          ? { ...r, approved_domains: domains, approved_emails: emails }
          : r,
      ),
    );
  }

  return (
    <div className="max-w-3xl py-6">
      <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
        Approved email recipients
      </h2>
      <p className="mt-1" style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
        Upmode sends from one shared mailbox, so a brand can only email the domains and
        addresses approved here — plus each team member's own address, which is always
        allowed. Anything else is refused before the mail is sent. Changes take effect on
        the next send.
      </p>

      {err && (
        <div
          className="mt-4 rounded-lg px-3 py-2"
          style={{ fontSize: "var(--text-sm)", color: "var(--error-text)", backgroundColor: "var(--error-bg)" }}
        >
          {err}
        </div>
      )}

      {loading ? (
        <div className="mt-5 flex items-center gap-2" style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading brands…
        </div>
      ) : (
        <>
          <div className="mt-5 flex items-center gap-2">
            <span
              className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: "var(--muted, #f1f5f9)", color: "var(--muted-foreground)" }}
            >
              <Search className="h-4 w-4" />
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by brand, member, domain or address"
              aria-label="Filter brands"
              className="flex-1 rounded-lg border px-3 py-2"
              style={{
                fontSize: "var(--text-sm)",
                color: "var(--foreground)",
                backgroundColor: "var(--card, transparent)",
                borderColor: "var(--border)",
              }}
            />
          </div>

          {filtered.length === 0 && (
            <p className="mt-4" style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
              {rows.length === 0 ? "No brands found." : "No brands match that filter."}
            </p>
          )}

          <div className="mt-4 space-y-2">
            {filtered.map((row) => (
              <BrandRow
                key={row.brand_key}
                row={row}
                open={expanded === row.brand_key}
                onToggle={() => setExpanded(expanded === row.brand_key ? null : row.brand_key)}
                onSaved={(domains, emails) => applySaved(row.brand_key, domains, emails)}
              />
            ))}
          </div>

          {orphans.length > 0 && (
            <OrphanList
              orphans={orphans}
              onCleared={(key) => setOrphans((prev) => prev.filter((o) => o.brand_key !== key))}
            />
          )}
        </>
      )}
    </div>
  );
}

// ─── One brand ────────────────────────────────────────────────────────────────

function BrandRow({
  row, open, onToggle, onSaved,
}: {
  row: BrandRecipientsRow;
  open: boolean;
  onToggle: () => void;
  onSaved: (domains: string[], emails: string[]) => void;
}) {
  // Drafts live here so an unsaved edit is discarded when the row is collapsed
  // — a half-typed domain must never look like it is in force.
  const [domains, setDomains] = useState<string[]>(row.approved_domains);
  const [emails, setEmails] = useState<string[]>(row.approved_emails);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    setDomains(row.approved_domains);
    setEmails(row.approved_emails);
    setErr("");
  }, [row.approved_domains, row.approved_emails, open]);

  const same = (a: string[], b: string[]) =>
    a.length === b.length && a.every((v, i) => v === b[i]);
  const dirty = !same(domains, row.approved_domains) || !same(emails, row.approved_emails);

  async function save() {
    if (saving) return;
    setSaving(true);
    setErr("");
    try {
      const stored = await upmodeAgentApi.setEmailRecipients(row.brand_name, domains, emails);
      onSaved(stored.domains, stored.emails);
      setDomains(stored.domains);
      setEmails(stored.emails);
      setSavedAt(Date.now());
    } catch (e: any) {
      // The server explains WHY (malformed, or a public webmail domain).
      setErr(String(e?.message || e));
      setDomains(row.approved_domains);   // never leave a rejected value looking saved
      setEmails(row.approved_emails);
    } finally {
      setSaving(false);
    }
  }

  const reach = row.approved_domains.length + row.approved_emails.length;

  return (
    <div className="rounded-lg border" style={{ borderColor: "var(--border)", backgroundColor: "var(--card, transparent)" }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-3 py-3 text-left"
        style={{ cursor: "pointer", background: "transparent" }}
      >
        {open ? <ChevronDown className="h-4 w-4 flex-shrink-0" style={{ color: "var(--muted-foreground)" }} />
              : <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: "var(--muted-foreground)" }} />}

        <span className="flex-1 truncate" style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-medium)", color: "var(--foreground)" }}>
          {row.brand_name}
          {row.aliases.length > 0 && (
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginLeft: 6 }}>
              also “{row.aliases.join("”, “")}”
            </span>
          )}
        </span>

        <Pill icon={<Users className="h-3 w-3" />} label={`${row.members.length}`} title={`${row.members.length} team member(s)`} />
        <Pill
          icon={<Globe className="h-3 w-3" />}
          label={`${row.approved_domains.length}`}
          title={`${row.approved_domains.length} approved domain(s)`}
          accent={row.approved_domains.length > 0}
        />
        <Pill
          icon={<Mail className="h-3 w-3" />}
          label={`${row.approved_emails.length}`}
          title={`${row.approved_emails.length} approved address(es)`}
          accent={row.approved_emails.length > 0}
        />
      </button>

      {open && (
        <div className="border-t px-3 py-4" style={{ borderColor: "var(--border)" }}>
          <FieldLabel>Team members — always allowed</FieldLabel>
          {row.members.length === 0 ? (
            <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
              No one is assigned to this brand. Assign users under Associate Brands.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {row.members.map((m) => (
                <Chip key={m.user_id} title={m.full_name || m.username}>{m.username}</Chip>
              ))}
            </div>
          )}

          <TokenField
            label="Approved domains"
            hint="Anyone at these domains can be emailed. This is usually all you need."
            placeholder="company.com"
            empty="None yet."
            values={domains}
            onChange={setDomains}
            normalize={normalizeDomain}
            validate={(v) => DOMAIN_RE.test(v)}
            render={(d) => `@${d}`}
          />

          <TokenField
            label="Specific addresses"
            hint="For one-off recipients outside those domains."
            placeholder="name@elsewhere.com"
            empty="None."
            values={emails}
            onChange={setEmails}
            normalize={(v) => v.trim().toLowerCase()}
            validate={(v) => EMAIL_RE.test(v)}
            render={(e) => e}
          />

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={saving || !dirty}
              onClick={save}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 transition-all"
              style={{
                cursor: saving || !dirty ? "default" : "pointer",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-weight-medium)",
                color: dirty ? "#fff" : "var(--muted-foreground)",
                backgroundColor: dirty ? "var(--alpha-teal)" : "var(--muted, #f1f5f9)",
              }}
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
            {reach === 0 && !dirty && (
              <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                Nothing approved — this brand can currently only email its own members.
              </span>
            )}
          </div>

          {err && (
            <p className="mt-2" style={{ fontSize: "var(--text-xs)", color: "var(--error-text)" }}>{err}</p>
          )}
          {savedAt > 0 && !saving && !err && !dirty && (
            <p className="mt-2 inline-flex items-center gap-1" style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              <Check className="h-3 w-3" />
              Saved. Applies to the next email {row.brand_name} sends.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── A list of chips with an add box ──────────────────────────────────────────

function TokenField({
  label, hint, placeholder, empty, values, onChange, normalize, validate, render,
}: {
  label: string;
  hint: string;
  placeholder: string;
  empty: string;
  values: string[];
  onChange: (next: string[]) => void;
  normalize: (raw: string) => string;
  validate: (normalized: string) => boolean;
  render: (value: string) => string;
}) {
  const [input, setInput] = useState("");
  const candidate = normalize(input);
  const canAdd = validate(candidate) && !values.includes(candidate);

  function add() {
    if (!canAdd) return;
    onChange([...values, candidate]);
    setInput("");
  }

  return (
    <div className="mt-4">
      <FieldLabel>{label}</FieldLabel>
      <p className="mb-2" style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>{hint}</p>

      {values.length === 0 ? (
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>{empty}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {values.map((value) => (
            <Chip key={value} accent onRemove={() => onChange(values.filter((v) => v !== value))}>
              {render(value)}
            </Chip>
          ))}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); add(); }
          }}
          placeholder={placeholder}
          aria-label={`Add to ${label}`}
          className="flex-1 min-w-[200px] rounded-lg border px-3 py-2"
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--foreground)",
            backgroundColor: "var(--card, transparent)",
            borderColor: input !== "" && !validate(candidate) ? "var(--error-border)" : "var(--border)",
          }}
        />
        <button
          type="button"
          disabled={!canAdd}
          onClick={add}
          className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2"
          style={{
            cursor: canAdd ? "pointer" : "default",
            fontSize: "var(--text-sm)",
            borderColor: "var(--border)",
            color: canAdd ? "var(--foreground)" : "var(--muted-foreground)",
            backgroundColor: "var(--card, transparent)",
          }}
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </button>
      </div>
    </div>
  );
}

// ─── Allow-lists whose brand is gone ──────────────────────────────────────────

function OrphanList({
  orphans, onCleared,
}: {
  orphans: OrphanRecipientsRow[];
  onCleared: (brandKey: string) => void;
}) {
  const [busy, setBusy] = useState("");

  async function clear(key: string) {
    setBusy(key);
    try {
      await upmodeAgentApi.setEmailRecipients(key, [], []);
      onCleared(key);
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="mt-5 rounded-lg border p-3" style={{ borderColor: "var(--border)" }}>
      <p className="inline-flex items-center gap-1.5" style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-medium)", color: "var(--foreground)" }}>
        <AlertTriangle className="h-3.5 w-3.5" />
        Lists with no matching brand
      </p>
      <p className="mt-1" style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
        Left behind by a renamed or deleted brand. They grant nothing, but clearing them keeps the
        configuration honest.
      </p>
      <div className="mt-3 space-y-2">
        {orphans.map((o) => (
          <div key={o.brand_key} className="flex flex-wrap items-center gap-2">
            <span style={{ fontSize: "var(--text-sm)", color: "var(--foreground)" }}>{o.brand_key}</span>
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              {[...o.approved_domains.map((d) => `@${d}`), ...o.approved_emails].join(", ")}
            </span>
            <button
              type="button"
              disabled={busy === o.brand_key}
              onClick={() => clear(o.brand_key)}
              className="inline-flex items-center gap-1.5 rounded-lg border px-2 py-1"
              style={{ cursor: "pointer", fontSize: "var(--text-xs)", borderColor: "var(--border)", color: "var(--error-text)" }}
            >
              {busy === o.brand_key ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
              Clear
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Small shared bits ────────────────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mb-1.5"
      style={{
        fontSize: 10, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.05em", color: "var(--muted-foreground)",
      }}
    >
      {children}
    </div>
  );
}

function Chip({
  children, title, accent = false, onRemove,
}: {
  children: React.ReactNode;
  title?: string;
  accent?: boolean;
  onRemove?: () => void;
}) {
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1"
      style={{
        fontSize: "var(--text-xs)",
        backgroundColor: accent ? "var(--alpha-teal-10)" : "var(--muted, #f1f5f9)",
        color: accent ? "var(--alpha-teal)" : "var(--muted-foreground)",
      }}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${String(children)}`}
          style={{ cursor: "pointer", background: "transparent", lineHeight: 0 }}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

function Pill({
  icon, label, title, accent = false,
}: {
  icon: React.ReactNode; label: string; title?: string; accent?: boolean;
}) {
  return (
    <span
      title={title}
      className="hidden sm:inline-flex flex-shrink-0 items-center gap-1 rounded-md px-2 py-1"
      style={{
        fontSize: "var(--text-xs)",
        backgroundColor: accent ? "var(--alpha-teal-10)" : "var(--muted, #f1f5f9)",
        color: accent ? "var(--alpha-teal)" : "var(--muted-foreground)",
      }}
    >
      {icon}
      {label}
    </span>
  );
}

export default EmailRecipientsSetting;
